import React, { useEffect, useState } from "react";
import { client } from "../client";
import { FaCheckCircle, FaTimesCircle, FaHourglassHalf, FaTrashAlt } from "react-icons/fa";

const UserNotifications = ({ user }) => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);

    // Status styles + icons
    const getStatusInfo = (status) => {
        switch (status) {
            case "approved":
                return {
                    classes: "border-green-500 bg-green-50 text-green-600",
                    icon: <FaCheckCircle className="text-green-600" />,
                    label: "Approved ✅",
                };
            case "rejected":
                return {
                    classes: "border-yellow-500 bg-yellow-50 text-yellow-600",
                    icon: <FaTimesCircle className="text-yellow-600" />,
                    label: "Rejected ❌",
                };
            case "removed":
                return {
                    classes: "border-red-500 bg-red-50 text-red-600",
                    icon: <FaTrashAlt className="text-red-600" />,
                    label: "Deleted 🗑️",
                };
            case "pending":
                return {
                    classes: "border-blue-500 bg-blue-50 text-blue-600",
                    icon: <FaHourglassHalf className="text-blue-600" />,
                    label: "Pending ⏳",
                };
            default:
                return { classes: "", icon: null, label: status };
        }
    };

    // Fetch all pins of this user
    const fetchNotifications = async () => {
        if (!user?._id) return;

        try {
            const query = `
            *[_type == "pin" && postedBy._ref == "${user._id}"] 
            | order(_updatedAt desc) {
              _id,
              title,
              status,
              adminReason,
              _updatedAt,
              image {
                asset -> { url }
              }
            }
          `;

            const data = await client.fetch(query);
            setNotifications(data);
        } catch (error) {
            console.error("Error fetching notifications:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotifications();
    }, [user]);

    if (!user?._id) {
        return <p className="text-center mt-10">Please log in to see notifications.</p>;
    }

    if (loading) {
        return <p className="text-center mt-10">Loading notifications...</p>;
    }

    if (notifications.length === 0) {
        return <p className="text-center mt-10">No notifications yet 🎉</p>;
    }

    return (
        <div className="p-5">
            <h1 className="text-2xl font-bold mb-5">Your Notifications</h1>
            <div className="space-y-4">
                {notifications.map((note) => {
                    const { classes, icon, label } = getStatusInfo(note.status);
                    return (
                        <div
                            key={note._id}
                            className={`p-4 rounded-lg shadow border flex items-center space-x-4 ${classes}`}
                        >
                            {note.image?.asset?.url && (
                                <img
                                    src={note.image.asset.url}
                                    alt={note.title}
                                    className="w-16 h-16 rounded object-cover"
                                />
                            )}
                            <div>
                                <h2 className="font-semibold text-lg">{note.title}</h2>
                                <p className="text-sm flex items-center gap-2">
                                    {icon} <span className="font-bold">{label}</span>
                                </p>
                                {note.adminReason && (
                                    <p className="text-sm text-gray-700">Reason: {note.adminReason}</p>
                                )}
                                {note._updatedAt && (
                                    <p className="text-xs text-gray-500">
                                        Updated: {new Date(note._updatedAt).toLocaleString()}
                                    </p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default UserNotifications;
